import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/helpers';
import {
  Upload, Download, CheckCircle2, XCircle, Clock,
  FileText, AlertTriangle, ChevronDown, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '';

const UPLOAD_TYPES = [
  {
    key:   'SALES_ORDERS',
    label: 'Sales Orders',
    icon:  '📋',
    desc:  'Import Sales Orders with payment terms. They arrive in APPROVED status ready to use.',
    fields: [
      { name: 'so_number',             req: false, desc: 'Unique SO number (auto-generated if blank)' },
      { name: 'title',                 req: true,  desc: 'Sales order title / description' },
      { name: 'customer_name',         req: true,  desc: 'Customer name (created if not exists)' },
      { name: 'po_value',              req: true,  desc: 'Total contract value (number)' },
      { name: 'currency',              req: false, desc: 'INR, USD, EUR, etc. (default: INR)' },
      { name: 'po_date',               req: false, desc: 'YYYY-MM-DD' },
      { name: 'delivery_date',         req: false, desc: 'YYYY-MM-DD' },
      { name: 'invoice_approval_mode', req: false, desc: 'DRAFT_FOR_REVIEW or AUTO_APPROVE' },
      { name: 'payment_term_name',     req: false, desc: 'Name for the payment term' },
      { name: 'payment_term_type',     req: false, desc: 'DELIVERY_BASED, SUBSCRIPTION, etc.' },
      { name: 'installment_label',     req: false, desc: 'Label for the installment' },
      { name: 'amount_type',           req: false, desc: 'FIXED_AMOUNT or PERCENTAGE' },
      { name: 'amount_value',          req: false, desc: 'Amount or percentage value' },
      { name: 'trigger_type',          req: false, desc: 'PO_CONFIRMATION, DELIVERY_ACCEPTANCE, TIME_PERIOD' },
      { name: 'scheduled_invoice_date',req: false, desc: 'YYYY-MM-DD (for TIME_PERIOD trigger)' },
      { name: 'service_period_from',   req: false, desc: 'YYYY-MM-DD (for revenue recognition)' },
      { name: 'service_period_to',     req: false, desc: 'YYYY-MM-DD' },
      { name: 'day_count_convention',  req: false, desc: 'ACTUAL_ACTUAL, ACTUAL_365, ACTUAL_360, THIRTY_360' },
    ],
  },
  {
    key:   'INVOICES',
    label: 'Invoices',
    icon:  '🧾',
    desc:  'Import historical invoices. They arrive in APPROVED status with revenue recognition schedules if service period is provided.',
    fields: [
      { name: 'so_number',            req: true,  desc: 'Must match an existing Sales Order' },
      { name: 'invoice_number',       req: false, desc: 'Unique invoice number (auto-generated if blank)' },
      { name: 'invoice_date',         req: true,  desc: 'YYYY-MM-DD' },
      { name: 'due_date',             req: false, desc: 'YYYY-MM-DD' },
      { name: 'amount',               req: true,  desc: 'Invoice amount (number)' },
      { name: 'currency',             req: false, desc: 'INR, USD, etc.' },
      { name: 'customer_name',        req: false, desc: 'For reference' },
      { name: 'service_period_from',  req: false, desc: 'YYYY-MM-DD — triggers revenue recognition schedule' },
      { name: 'service_period_to',    req: false, desc: 'YYYY-MM-DD' },
      { name: 'day_count_convention', req: false, desc: 'ACTUAL_ACTUAL (default), ACTUAL_365, ACTUAL_360, THIRTY_360' },
      { name: 'notes',                req: false, desc: 'Optional notes' },
    ],
  },
  {
    key:   'PAYMENTS',
    label: 'Payments',
    icon:  '💳',
    desc:  'Record bulk payment receipts against existing invoices.',
    fields: [
      { name: 'invoice_number',   req: true,  desc: 'Must match an existing invoice' },
      { name: 'payment_date',     req: true,  desc: 'YYYY-MM-DD' },
      { name: 'amount',           req: true,  desc: 'Payment amount (number)' },
      { name: 'currency',         req: false, desc: 'INR, USD, etc.' },
      { name: 'reference_number', req: false, desc: 'NEFT/RTGS/Cheque reference' },
      { name: 'notes',            req: false, desc: 'Optional notes' },
    ],
  },
  {
    key:   'UNBILLED_REVENUE',
    label: 'Unbilled Revenue',
    icon:  '📊',
    desc:  'Import unbilled revenue entries with revenue recognition schedules (Ind AS 115 Scenario 2).',
    fields: [
      { name: 'so_number',            req: true,  desc: 'Must match an existing Sales Order' },
      { name: 'customer_name',        req: false, desc: 'For reference' },
      { name: 'amount',               req: true,  desc: 'Amount (number)' },
      { name: 'currency',             req: false, desc: 'INR, USD, etc.' },
      { name: 'service_period_from',  req: true,  desc: 'YYYY-MM-DD' },
      { name: 'service_period_to',    req: true,  desc: 'YYYY-MM-DD' },
      { name: 'day_count_convention', req: false, desc: 'ACTUAL_ACTUAL (default)' },
      { name: 'expected_invoice_date',req: false, desc: 'YYYY-MM-DD' },
      { name: 'notes',                req: false, desc: 'Optional notes' },
    ],
  },
];

export default function BulkUpload() {
  const [activeType, setActiveType] = useState('SALES_ORDERS');
  const [uploading, setUploading]   = useState(false);
  const [result, setResult]         = useState(null);
  const [history, setHistory]       = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();

  const token = localStorage.getItem('token');

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/bulk-upload/template/${activeType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${activeType.toLowerCase()}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setResult(null);
    setUploading(true);
    try {
      const text = await file.text();
      const res  = await fetch(`${API_URL}/bulk-upload/${activeType}`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ csv_data: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
      if (data.success_count > 0) {
        toast.success(`${data.success_count} record(s) imported successfully`);
      }
      if (data.error_count > 0) {
        toast.error(`${data.error_count} record(s) failed — see details below`);
      }
    } catch (err) {
      toast.error(err.message);
      setResult({ error: err.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const loadHistory = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/bulk-upload/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setHistory(data.uploads || []);
      setShowHistory(true);
    } catch {}
  }, [token]);

  const activeTypeInfo = UPLOAD_TYPES.find(t => t.key === activeType);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Bulk Upload</h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--bt-gray-500)' }}>
          Import historical data or onboard in bulk. Download the CSV template, fill it in, and upload.
        </p>
      </div>

      {/* Type selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {UPLOAD_TYPES.map(t => (
          <button key={t.key} onClick={() => { setActiveType(t.key); setResult(null); }}
            className="p-4 rounded-xl border text-left transition-all"
            style={{
              borderColor: activeType === t.key ? 'var(--bt-orange)' : 'var(--bt-gray-200)',
              background:  activeType === t.key ? 'var(--bt-orange-light)' : '#fff',
            }}>
            <div className="text-2xl mb-1">{t.icon}</div>
            <div className="font-semibold text-sm" style={{ color: activeType === t.key ? 'var(--bt-orange)' : 'var(--bt-gray-800)' }}>
              {t.label}
            </div>
          </button>
        ))}
      </div>

      {/* Active type detail */}
      <div className="bt-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-semibold text-lg">{activeTypeInfo.icon} {activeTypeInfo.label}</h3>
            <p className="text-sm mt-1" style={{ color: 'var(--bt-gray-500)' }}>{activeTypeInfo.desc}</p>
          </div>
          <button onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm"
            style={{ background: 'var(--bt-orange-light)', color: 'var(--bt-orange)', border: '1px solid var(--bt-orange-border)' }}>
            <Download size={15}/> Download Template
          </button>
        </div>

        {/* Field reference */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium" style={{ color: 'var(--bt-gray-600)' }}>
            📋 Column reference ({activeTypeInfo.fields.length} fields)
          </summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr style={{ background: 'var(--bt-gray-50)' }}>
                  {['Column Name','Required','Description'].map(h => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeTypeInfo.fields.map(f => (
                  <tr key={f.name}>
                    <td className="table-td font-mono font-medium">{f.name}</td>
                    <td className="table-td">
                      {f.req
                        ? <span style={{ color: 'var(--bt-red)', fontWeight: 600 }}>Required</span>
                        : <span style={{ color: 'var(--bt-gray-400)' }}>Optional</span>
                      }
                    </td>
                    <td className="table-td" style={{ color: 'var(--bt-gray-600)' }}>{f.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>

        {/* Upload area */}
        <div className="mt-5">
          <div
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
            style={{ borderColor: 'var(--bt-gray-300)', background: 'var(--bt-gray-50)' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--bt-orange)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--bt-gray-300)'}
          >
            {uploading ? (
              <>
                <div className="w-10 h-10 rounded-full border-4 border-orange-200 border-t-orange-500 animate-spin"/>
                <p className="text-sm font-medium" style={{ color: 'var(--bt-gray-600)' }}>Processing upload…</p>
              </>
            ) : (
              <>
                <Upload size={32} style={{ color: 'var(--bt-gray-400)' }}/>
                <div className="text-center">
                  <p className="font-medium text-sm">
                    <span style={{ color: 'var(--bt-orange)' }}>Click to upload</span>
                    {' '}or drag &amp; drop
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>CSV files only</p>
                </div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange}/>
        </div>
      </div>

      {/* Result */}
      {result && !result.error && (
        <div className="bt-card overflow-hidden">
          <div className="p-4 border-b flex items-center gap-3"
            style={{ background: result.error_count > 0 ? '#FFF7ED' : '#F0FDF4' }}>
            {result.error_count === 0
              ? <CheckCircle2 size={20} style={{ color: '#16A34A' }}/>
              : <AlertTriangle size={20} style={{ color: '#D97706' }}/>
            }
            <div>
              <p className="font-semibold">
                {result.success_count} imported
                {result.error_count > 0 && `, ${result.error_count} failed`}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--bt-gray-500)' }}>{result.message}</p>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="p-4">
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--bt-red)' }}>
                Errors ({result.errors.length}):
              </p>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs p-2 rounded-lg"
                    style={{ background: '#FEF2F2' }}>
                    <XCircle size={13} style={{ color: 'var(--bt-red)', flexShrink: 0, marginTop: 1 }}/>
                    <span>
                      <strong>Row {e.row}</strong>
                      {e.so_number && ` (${e.so_number})`}
                      {e.invoice_number && ` (${e.invoice_number})`}
                      : {e.error}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {result?.error && (
        <div className="bt-card p-4 flex items-center gap-3" style={{ background: '#FEF2F2' }}>
          <XCircle size={18} style={{ color: 'var(--bt-red)' }}/>
          <p className="text-sm font-medium" style={{ color: '#DC2626' }}>{result.error}</p>
        </div>
      )}

      {/* Upload history */}
      <div>
        <button onClick={loadHistory}
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--bt-gray-600)', background: 'none', border: 'none', cursor: 'pointer' }}>
          {showHistory ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}
          Upload History
        </button>
        {showHistory && (
          <div className="bt-card overflow-hidden mt-2">
            {history.length === 0 ? (
              <p className="p-5 text-sm text-center" style={{ color: 'var(--bt-gray-400)' }}>No uploads yet</p>
            ) : (
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 140 }}/><col style={{ width: 120 }}/><col/>
                  <col style={{ width: 80 }}/><col style={{ width: 80 }}/><col style={{ width: 90 }}/>
                </colgroup>
                <thead>
                  <tr>{['Date','Type','Uploaded By','Success','Failed','Status'].map(h =>
                    <th key={h} className="table-th">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.upload_id}>
                      <td className="table-td text-xs">{formatDateTime(h.created_at)}</td>
                      <td className="table-td text-xs">{h.upload_type}</td>
                      <td className="table-td text-xs">{h.uploaded_by_name || '—'}</td>
                      <td className="table-td text-green-600 font-semibold">{h.success_count}</td>
                      <td className="table-td text-red-500">{h.error_count > 0 ? h.error_count : '—'}</td>
                      <td className="table-td">
                        {h.status === 'COMPLETED'
                          ? <span className="bt-badge bt-badge-green">Done</span>
                          : h.status === 'FAILED'
                          ? <span className="bt-badge bt-badge-red">Failed</span>
                          : <span className="bt-badge bt-badge-amber">Processing</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
